import { useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { X, Trash2, ChevronLeft } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useAdrDetailSidebarResize } from '../hooks/useDetailSidebarResize';
import { useAdrForm } from '../../adr/useAdrForm';
import { AdrFormTabs } from '../../adr/components/AdrFormTabs';

// ============================================
// AdrDetailSidebar (outer shell)
// ============================================

export const AdrDetailSidebar = memo(function AdrDetailSidebar() {
  const { t } = useTranslation();
  const { uiStore } = useCanvasStores();
  const adrDetailAdrId = useSelector(uiStore, s => s.context.adrDetailAdrId);
  const adrDetailWidth = useSelector(uiStore, s => s.context.adrDetailWidth);
  const isAdrDetailCollapsed = useSelector(uiStore, s => s.context.isAdrDetailCollapsed);
  const isAdrDetailResizing = useSelector(uiStore, s => s.context.isAdrDetailResizing);

  const { handleResizeStart, handleDoubleClick, toggleCollapse } = useAdrDetailSidebarResize(uiStore);

  // Not visible when no ADR is selected
  if (adrDetailAdrId === null) return null;

  // Collapsed state: thin strip with expand button
  if (isAdrDetailCollapsed) {
    return (
      <div
        style={{
          width: 24,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #e2e8f0',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: 32,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={toggleCollapse}
          title={t('adr.detailSidebar.expandSidebar')}
        >
          <ChevronLeft size={12} style={{ color: '#64748b' }} />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={toggleCollapse}
        >
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 11,
              fontWeight: 500,
              color: '#64748b',
              letterSpacing: '0.05em',
            }}
          >
            ADR
          </span>
        </div>

        {/* Resize Handle (left edge) */}
        <div
          onMouseDown={handleResizeStart}
          onDoubleClick={handleDoubleClick}
          style={{
            position: 'absolute',
            top: 0,
            left: -2,
            width: 4,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 31,
            backgroundColor: 'transparent',
          }}
        />
      </div>
    );
  }

  // Expanded state
  return (
    <div
      style={{
        width: adrDetailWidth,
        flexShrink: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #e2e8f0',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <AdrDetailContent adrId={adrDetailAdrId} />

      {/* Resize Handle (left edge) */}
      <div
        onMouseDown={handleResizeStart}
        onDoubleClick={handleDoubleClick}
        style={{
          position: 'absolute',
          top: 0,
          left: -2,
          width: 4,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 31,
          backgroundColor: isAdrDetailResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isAdrDetailResizing) {
            e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isAdrDetailResizing) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      />
    </div>
  );
});

// ============================================
// AdrDetailContent (inner form using shared hook + tabs)
// ============================================

function AdrDetailContent({ adrId }: { adrId: string }) {
  const { t } = useTranslation();
  const { uiStore } = useCanvasStores();
  const form = useAdrForm(adrId);

  // Auto-close if the ADR doesn't exist in the store (e.g. deleted, stale reference)
  useEffect(() => {
    if (!form.existingAdr) {
      uiStore.send({ type: 'adrDetail.close' });
    }
  }, [form.existingAdr, uiStore]);

  const handleClose = useCallback(() => {
    form.flushEditors();
    uiStore.send({ type: 'adrDetail.close' });
  }, [form, uiStore]);

  const handleDelete = useCallback(() => {
    if (form.deleteAdr()) {
      uiStore.send({ type: 'adrDetail.close' });
    }
  }, [form, uiStore]);

  // Keyboard: Escape closes sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 shrink-0">
        <span className="text-xs font-medium text-gray-500">
          {t('adr.detailSidebar.title')}
        </span>
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <AdrFormTabs form={form} px="px-3" />

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {form.loadedAdrId && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              {t('common.delete')}
            </button>
          )}
        </div>
        <span className="text-[10px] text-gray-400">ESC</span>
      </div>
    </>
  );
}
